import React, { useState } from 'react';
import { Button, Form, Input, message, Space, Tag, Upload } from 'antd';
import { CameraOutlined, FileTextOutlined, FormOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { essayAPI, topicAPI } from '../../services/api';
import StudentSelector from '../../components/StudentSelector';
import TopicSelector from '../../components/TopicSelector';
import { EssayTopic } from '../../types';
import { extractTopicData } from '../../utils/topicHelper';

const { TextArea } = Input;

const H5EssayWizard: React.FC = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [studentInfo, setStudentInfo] = useState<any>({});
  const [selectedTopic, setSelectedTopic] = useState<EssayTopic | null>(null);
  const [essayData, setEssayData] = useState<any>(null);
  const [ocrText, setOcrText] = useState('');
  const [manualText, setManualText] = useState('');
  const [form] = Form.useForm();

  const steps = [
    { title: '学生', icon: <UserOutlined /> },
    { title: '题目', icon: <FormOutlined /> },
    { title: '作文', icon: <CameraOutlined /> },
    { title: '确认', icon: <FileTextOutlined /> },
  ];

  const nextFromStudent = () => {
    if (!studentInfo.studentId || !studentInfo.classId) {
      message.warning('请先选择学生');
      return;
    }
    setCurrent(1);
  };

  const createEssay = async (values: any) => {
    if (!studentInfo.studentId || !studentInfo.classId) {
      message.warning('请先选择学生');
      setCurrent(0);
      return;
    }

    setLoading(true);
    try {
      const topicResponse = await topicAPI.get(values.topic_id);
      const topicData = extractTopicData(topicResponse.data.data);
      const response = await essayAPI.createWithStudent({
        topic_id: values.topic_id,
        essay_type: topicData?.essay_type,
        student_id: studentInfo.studentId,
        class_id: studentInfo.classId,
      });
      setSelectedTopic(topicData);
      setEssayData({ ...response.data.essay, topic: topicData });
      setCurrent(2);
    } catch (error: any) {
      message.error(error.response?.data?.error || '创建作文失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!essayData?.id) {
      message.warning('请先创建作文');
      return false;
    }

    setLoading(true);
    try {
      const uploadResponse = await essayAPI.upload(essayData.id, file);
      if (uploadResponse.data.needs_ocr === false && uploadResponse.data.text) {
        setOcrText(uploadResponse.data.text);
      } else if (uploadResponse.data.needs_ocr) {
        const ocrResponse = await essayAPI.ocr(essayData.id);
        setOcrText(ocrResponse.data.text || '');
      } else {
        setOcrText(uploadResponse.data.text || '');
      }
      message.success('上传完成，请确认文本');
      setCurrent(3);
    } catch (error: any) {
      message.error(error.response?.data?.error || '上传失败');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const useManualText = async () => {
    if (!manualText.trim()) {
      message.warning('请输入作文内容');
      return;
    }
    setOcrText(manualText.trim());
    setCurrent(3);
  };

  const submitCorrection = async () => {
    if (!essayData?.id || !ocrText.trim()) {
      message.warning('请先确认作文文本');
      return;
    }

    setLoading(true);
    try {
      await essayAPI.update(essayData.id, { corrected_text: ocrText.trim() });
      await essayAPI.correct(essayData.id);
      message.success('批改完成');
      navigate(`/h5/essay/${essayData.id}/result`);
    } catch (error: any) {
      message.error(error.response?.data?.error || '批改失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h5-page h5-wizard-page">
      <section className="h5-panel h5-step-panel">
        <div className="h5-stepper">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className={`h5-stepper-item ${index === current ? 'active' : ''} ${index < current ? 'done' : ''}`}
            >
              <div className="h5-stepper-icon">{step.icon}</div>
              <span>{step.title}</span>
            </div>
          ))}
        </div>
      </section>

      {current === 0 && (
        <section className="h5-panel">
          <div className="h5-section-title">
            <h2>选择学生</h2>
          </div>
          <StudentSelector
            className="h5-student-selector"
            onChange={(value) => setStudentInfo(value)}
            placeholder="输入学生姓名搜索"
          />
          {studentInfo.studentName && (
            <div className="h5-selected-row">
              <Tag color="green">已选择 {studentInfo.studentName}</Tag>
            </div>
          )}
          <Button type="primary" block size="large" onClick={nextFromStudent}>
            下一步
          </Button>
        </section>
      )}

      {current === 1 && (
        <section className="h5-panel">
          <div className="h5-section-title">
            <h2>设定题目</h2>
          </div>
          <Form form={form} layout="vertical" onFinish={createEssay}>
            <Form.Item name="topic_id" label="作文题目" rules={[{ required: true, message: '请选择题目' }]}>
              <TopicSelector
                onChange={(topicId, topic) => {
                  form.setFieldsValue({ topic_id: topicId });
                  if (topic) setSelectedTopic(topic);
                }}
              />
            </Form.Item>
            {selectedTopic && (
              <div className="h5-topic-preview">
                <strong>{selectedTopic.title}</strong>
                <span>{selectedTopic.requirements || selectedTopic.topic || '暂无详细要求'}</span>
              </div>
            )}
            <Space className="h5-action-grid">
              <Button onClick={() => setCurrent(0)}>上一步</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                创建作文
              </Button>
            </Space>
          </Form>
        </section>
      )}

      {current === 2 && (
        <section className="h5-panel">
          <div className="h5-section-title">
            <h2>上传作文</h2>
          </div>
          <div className="h5-context-tags">
            <Tag color="blue">{studentInfo.studentName}</Tag>
            <Tag color="green">{selectedTopic?.title || '未设置题目'}</Tag>
          </div>

          <div className="h5-upload-grid">
            <Upload accept="image/*" capture="environment" showUploadList={false} beforeUpload={handleUpload}>
              <Button type="primary" size="large" icon={<CameraOutlined />} loading={loading} block>
                拍照上传
              </Button>
            </Upload>
            <Upload accept="image/*,.pdf,.docx,.doc,.txt" showUploadList={false} beforeUpload={handleUpload}>
              <Button size="large" icon={<FileTextOutlined />} loading={loading} block>
                相册/文件
              </Button>
            </Upload>
          </div>

          <div className="h5-divider">或粘贴作文文本</div>
          <TextArea
            rows={8}
            value={manualText}
            onChange={(event) => setManualText(event.target.value)}
            placeholder="也可以直接粘贴学生作文文本"
          />
          <Space className="h5-action-grid">
            <Button onClick={() => setCurrent(1)}>上一步</Button>
            <Button type="primary" onClick={useManualText}>
              使用文本
            </Button>
          </Space>
        </section>
      )}

      {current === 3 && (
        <section className="h5-panel">
          <div className="h5-section-title">
            <h2>确认内容</h2>
          </div>
          <TextArea
            className="h5-confirm-textarea"
            rows={14}
            value={ocrText}
            onChange={(event) => setOcrText(event.target.value)}
            placeholder="请确认识别后的作文文本"
          />
          <Space className="h5-action-grid">
            <Button onClick={() => setCurrent(2)}>上一步</Button>
            <Button type="primary" loading={loading} onClick={submitCorrection}>
              提交批改
            </Button>
          </Space>
        </section>
      )}
    </div>
  );
};

export default H5EssayWizard;
